import { selectLocales } from "hooks/useAdapterLocale";
import { t } from "i18next";

const getTranslationNameExamples = () => {
    const locales = selectLocales();
    const keys = {};
    for (const locale of locales) {
        keys[`name_${locale.code}`] = t(`ImportDataTemplateExamples.name_${locale.code}`);
    }
    return keys;
};
/**
 * Examples for the template files.
 * Translated values are getters so they follow the active (country) language, not the one at module load
 * (locale name examples are still resolved once, at load).
 */
export const templateExamples: Record<ExampleKeys, string> = {
    ean13: "9891291299121",
    get name() {
        return t("ImportDataTemplateExamples.name");
    },
    ...getTranslationNameExamples(),
    get price() {
        return t("ImportDataTemplateExamples.price");
    },
    get releaseDate() {
        return t("ImportDataTemplateExamples.releaseDate");
    },
    initialInventory: "4000",
    analysisCode: "AKODE4563",
    artworkNature: "001",
    get summary() {
        return t("ImportDataTemplateExamples.summary");
    },
    get tags() {
        return t("ImportDataTemplateExamples.tags");
    },
    channelId: "456",
    channelISBN: "99589877856745",
    get channelBookTitle() {
        return t("ImportDataTemplateExamples.channelBookTitle");
    },
    channelReleaseDate: "2014-03-16",
    cumulativeSales: "3000",
    quantity: "3000",
    channelPrice: "45.5",
    get brand() {
        return t("ImportDataTemplateExamples.brand");
    },
    get compensationGroup() {
        return t("ImportDataTemplateExamples.compensationGroup");
    },
    currency: "EUR",
    channelCurrency: "EUR",
    get deliveryDate() {
        return t("ImportDataTemplateExamples.deliveryDate");
    },
    get articleName() {
        return t("ImportDataTemplateExamples.articleName");
    },
    beneficiaryId: "123",
    articleWorkNature: "TEXTE",
    grossDuty: "300.50",
    get paidStatus() {
        return t("ImportDataTemplateExamples.paidStatus");
    },
    get paidAt() {
        return t("ImportDataTemplateExamples.paidAt");
    },
    get parutionCode() {
        return t("ImportDataTemplateExamples.parutionCode");
    },
    issn: "1234-5678",
    get issueName() {
        return t("ImportDataTemplateExamples.issueName");
    },
    articleQuantity: "7",
    articlePrice: "18.50",
    uniqueCode: "1234-5678",
    packagePrice: "90",
    reference: "JOHNDOE2023",
    contractTypology: "AUT",
    termReference: "JOHNDOE2023",
    beneficiaryCode: "45",
    get beneficiaryName() {
        return t("ImportDataTemplateExamples.beneficiaryName");
    },
    get beneficiarySurname() {
        return t("ImportDataTemplateExamples.beneficiarySurname");
    },
    get pseudonym() {
        return t("ImportDataTemplateExamples.pseudonym");
    },
    get street() {
        return t("ImportDataTemplateExamples.street");
    },
    zipCode: "75000",
    get city() {
        return t("ImportDataTemplateExamples.city");
    },
    get country() {
        return t("ImportDataTemplateExamples.country");
    },
    get phoneNumber() {
        return t("ImportDataTemplateExamples.phoneNumber");
    },
    get birthDate() {
        return t("ImportDataTemplateExamples.birthDate");
    },
    get activityType() {
        return t("ImportDataTemplateExamples.activityType");
    },
    get email() {
        return t("ImportDataTemplateExamples.email");
    },
    siretNumber: "12345678901234",
    socialSecurityNumber: "099887766655544",
    socialProfileType: "1",
    get vat() {
        return t("ImportDataTemplateExamples.vat");
    },
    get iban() {
        return t("ImportDataTemplateExamples.iban");
    },
    swift: "3300",
    entity: "physical",
    institution_number: "001",
    transit_number: "12345",
    account_number: "12345678",
    sort_code: "123456",
    get ribOwnerName() {
        return t("ImportDataTemplateExamples.ribOwnerName");
    },
    get paypalEmail() {
        return t("ImportDataTemplateExamples.paypalEmail");
    },
    bankCheck: "1",
    get accountingTypology() {
        return t("ImportDataTemplateExamples.accountingTypology");
    },
    accountingCode: "622331",
    get precount() {
        return t("ImportDataTemplateExamples.precount");
    },
    amount: "100",
    altMilestoneAmount: "55",
    get note() {
        return t("ImportDataTemplateExamples.note");
    },
    get termNote() {
        return t("ImportDataTemplateExamples.termNote");
    },
    returnedSales: "3",
    source: "Amazon",
    get saleDate() {
        return t("ImportDataTemplateExamples.saleDate");
    },
    get countrySale() {
        return t("ImportDataTemplateExamples.countrySale");
    },
    overridenPrice: "10.00",
    priceCurrency: "EUR",
    rate: "40%",
    distributorGrossPrice: "10.00",
    turnover: "1240",
    forfaitAmount: "300",
    get representativeName() {
        return t("ImportDataTemplateExamples.representativeName");
    },
    initialAdvance: "-3000",
    initialBalance: "-3000",
    initialProvision: "500",
    cumulativeRights: "2000",
    get compensate() {
        return t("ImportDataTemplateExamples.compensate");
    },
    get calculationBaseColumn() {
        return t("ImportDataTemplateExamples.calculationBaseColumn");
    },
    get milestoneBaseColumn() {
        return t("ImportDataTemplateExamples.milestoneBaseColumn");
    },

    channelMilestone_1: "1000",
    channelPercentage_1: "10",
    channelMilestone_2: "2000",
    channelPercentage_2: "12",
    channelMilestone_3: "3000",
    channelPercentage_3: "15",
    channelMilestone_4: "4000",
    channelPercentage_4: "20",
    channelMilestone_5: "infinity",
    channelPercentage_5: "25",
    channelSubscriptionCoefficient: "1.5",
    provision: "30%",
    reinstatedProvision: "20%",
    inventory: "60",
    pressInventory: "8",
    scrappedInventory: "2",
    printedInventory: "70",
    termId: "123",
    get comment() {
        return t("ImportDataTemplateExamples.comment");
    },
    get customStatus() {
        return t("ImportDataTemplateExamples.customStatus");
    },

    advanceAmount: "600",
    get completionStatus() {
        return t("ImportDataTemplateExamples.completionStatus");
    },
    sku: "LPTH_78",
    get isForfait() {
        return t("ImportDataTemplateExamples.isForfait");
    },
    titleId: "789",
    workId: "42",
    coveredTitleIds: "12,15,18",
    workName: t("Works.WorkNamePlaceholder"),
    workCode: "HOBBIT-01",
    formatType: t("ImportDataTemplateExamples.formatType"),
    formatTypeId: "12",
    consumptionCount: "1500",
    avgConsumptionDuration: "180",
    coefficient: "1.5",
    bank_account_country: "US",
    intl_swift: "CHASUS33",
    intl_account_number: "000123456789",
    intl_routing_code: "021000021",
    intl_bank_name: "JPMorgan Chase",
    collectiveManagementKind: "SOFIA",
};

type ExampleKeys =
    | "ean13"
    | "name"
    | "price"
    | "releaseDate"
    | "initialInventory"
    | "analysisCode"
    | "artworkNature"
    | "summary"
    | "tags"
    | "channelId"
    | "channelISBN"
    | "channelBookTitle"
    | "channelReleaseDate"
    | "cumulativeSales"
    | "quantity"
    | "channelPrice"
    | "brand"
    | "compensationGroup"
    | "currency"
    | "channelCurrency"
    | "deliveryDate"
    | "articleName"
    | "beneficiaryId"
    | "articleWorkNature"
    | "grossDuty"
    | "paidStatus"
    | "paidAt"
    | "parutionCode"
    | "issn"
    | "issueName"
    | "articleQuantity"
    | "articlePrice"
    | "uniqueCode"
    | "packagePrice"
    | "reference"
    | "contractTypology"
    | "termReference"
    | "beneficiaryCode"
    | "beneficiaryName"
    | "beneficiarySurname"
    | "pseudonym"
    | "street"
    | "zipCode"
    | "city"
    | "country"
    | "phoneNumber"
    | "birthDate"
    | "activityType"
    | "email"
    | "siretNumber"
    | "socialSecurityNumber"
    | "socialProfileType"
    | "vat"
    | "iban"
    | "swift"
    | "entity"
    | "institution_number"
    | "transit_number"
    | "account_number"
    | "sort_code"
    | "ribOwnerName"
    | "paypalEmail"
    | "bankCheck"
    | "accountingTypology"
    | "accountingCode"
    | "precount"
    | "amount"
    | "altMilestoneAmount"
    | "note"
    | "termNote"
    | "returnedSales"
    | "source"
    | "saleDate"
    | "countrySale"
    | "overridenPrice"
    | "priceCurrency"
    | "rate"
    | "turnover"
    | "forfaitAmount"
    | "representativeName"
    | "initialAdvance"
    | "initialBalance"
    | "initialProvision"
    | "cumulativeRights"
    | "compensate"
    | "calculationBaseColumn"
    | "milestoneBaseColumn"
    | "channelMilestone_1"
    | "channelPercentage_1"
    | "channelMilestone_2"
    | "channelPercentage_2"
    | "channelMilestone_3"
    | "channelPercentage_3"
    | "channelMilestone_4"
    | "channelPercentage_4"
    | "channelMilestone_5"
    | "channelPercentage_5"
    | "channelSubscriptionCoefficient"
    | "provision"
    | "reinstatedProvision"
    | "inventory"
    | "pressInventory"
    | "scrappedInventory"
    | "printedInventory"
    | "termId"
    | "comment"
    | "customStatus"
    | "advanceAmount"
    | "completionStatus"
    | "sku"
    | "isForfait"
    | "distributorGrossPrice"
    | "titleId"
    | "workId"
    | "coveredTitleIds"
    | "workName"
    | "workCode"
    | "formatType"
    | "formatTypeId"
    | "consumptionCount"
    | "avgConsumptionDuration"
    | "coefficient"
    | "bank_account_country"
    | "intl_swift"
    | "intl_account_number"
    | "intl_routing_code"
    | "intl_bank_name"
    | "collectiveManagementKind";
